import { useCallback, useEffect, useRef, useState } from "react";
import type { InstagramConnectionResponse } from "@multi-chat/shared";
import { isApiError, isBackendUnavailable } from "../../api/client.js";
import {
  disconnectInstagram as disconnectApi,
  getInstagramConnection as getConnectionApi,
} from "../../api/instagram-api.js";

export type InstagramView =
  | { kind: "loading" }
  | { kind: "unavailable"; message: string }
  | { kind: "ready"; connection: InstagramConnectionResponse }
  | { kind: "error"; message: string; code?: string };

interface UseInstagramConnectionResult {
  view: InstagramView;
  refresh: () => void;
  disconnect: () => Promise<void>;
  busy: boolean;
  actionError: string | null;
  clearActionError: () => void;
}

export function useInstagramConnection(
  organizationId: number,
  unreachableMessage: string,
): UseInstagramConnectionResult {
  const [view, setView] = useState<InstagramView>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const orgIdRef = useRef(organizationId);
  orgIdRef.current = organizationId;
  const unreachableRef = useRef(unreachableMessage);
  unreachableRef.current = unreachableMessage;

  const clearActionError = useCallback(() => setActionError(null), []);

  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

  const toErrorView = useCallback((error: unknown): InstagramView => {
    if (isBackendUnavailable(error)) {
      return { kind: "unavailable", message: unreachableRef.current };
    }
    if (isApiError(error)) {
      return { kind: "error", message: error.message, code: error.code };
    }
    return { kind: "unavailable", message: unreachableRef.current };
  }, []);

  const toActionError = useCallback((error: unknown): string => {
    if (isBackendUnavailable(error)) return unreachableRef.current;
    if (isApiError(error)) return error.message;
    return unreachableRef.current;
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const currentOrgId = orgIdRef.current;
    setView({ kind: "loading" });
    setActionError(null);

    (async () => {
      try {
        const connection = await getConnectionApi(currentOrgId, controller.signal);
        if (controller.signal.aborted) return;
        setView({ kind: "ready", connection });
      } catch (error) {
        if (controller.signal.aborted) return;
        setView(toErrorView(error));
      }
    })();

    return () => controller.abort();
  }, [organizationId, reloadToken, toErrorView]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      const connection = await disconnectApi(orgIdRef.current);
      setView({ kind: "ready", connection });
    } catch (error) {
      setActionError(toActionError(error));
    } finally {
      setBusy(false);
    }
  }, [toActionError]);

  return {
    view,
    refresh,
    disconnect,
    busy,
    actionError,
    clearActionError,
  };
}
